import * as React from 'react'
import { useMutation } from '@apollo/react-hooks'

import {
  HorizontalAlignment,
  i18n,
  RateLimitBucket,
  useCaptcha,
  useLogger,
  useRequestConfig,
  useSiteConfig,
} from '@thg-commerce/enterprise-core'
import { MutationSignUpForEmailMarketingCampaignArgs } from '@thg-commerce/enterprise-network/src/generated/graphql'
import { EnableSignUp } from '@thg-commerce/enterprise-widgets/src/WaitlistSignupWidgetRenderer/WaitlistSignupWidgetRenderer'
import { Checkbox, FormItem, Input } from '@thg-commerce/gravity-elements'
import { DynamicForm } from '@thg-commerce/gravity-patterns'

import { SignUpForEmailMarketingCampaign as SIGN_UP_FOR_MARKETING_CAMPAIGN_MUTATION } from '../../widgets/graphql/Mutation/SignUpForEmailMarketingCampaign.graphql'

import {
  DisclaimerText,
  StyledNameFormItemWrapper,
  SubmitButton,
  Subtitle,
  Title,
} from './styles'
import { FormStatus } from './WaitlistSignup'

interface SubmitValues {
  name: string
  emailAddress: string
  marketingConsentCheckbox: boolean
}

interface WaitlistSignupFormProps {
  title?: {
    text?: string
    horizontalAlignment?: HorizontalAlignment
  }
  subtitle?: {
    text?: string
    horizontalAlignment?: HorizontalAlignment
  }
  buttonText?: string
  campaignCode: string
  gdprDisclaimerText?: string
  emailPlaceholderText?: string
  fullNamePlaceholderText?: string
  setFormStatus: (status: FormStatus) => void
  enableSignUp?: EnableSignUp
  signUpCheckboxLabel?: string
}

export const WaitlistSignupForm = (props: WaitlistSignupFormProps) => {
  const captchaContainerRef = React.useRef<HTMLDivElement | null>(null)

  const logger = useLogger()
  const { csrf } = useRequestConfig()
  const captcha = useCaptcha({
    bucket: RateLimitBucket.MARKETING_MATERIAL_SIGN_UP,
    containerRef: captchaContainerRef,
  })
  const { showFullNameFieldOnWaitlist } = useSiteConfig()

  const i18nText = {
    requiredFieldText:
      i18n('general.form.required.field') || '* Required field',
    emailFieldName: i18n('form.fieldname.email'),
    fullNameFieldName: i18n('form.fieldname.fullname'),
    submitButtonText: i18n('general.signup.text'),
  }

  const [signUpForEmailMarketingCampaign] = useMutation<
    { signUpForEmailMarketingCampaign: { errors: string } | undefined },
    MutationSignUpForEmailMarketingCampaignArgs
  >(SIGN_UP_FOR_MARKETING_CAMPAIGN_MUTATION, {
    onCompleted: (data) => {
      if (
        data.signUpForEmailMarketingCampaign &&
        !data.signUpForEmailMarketingCampaign.errors
      ) {
        props.setFormStatus(FormStatus.SUCCESSFUL)
      } else {
        logger.error(
          'signUpForEmailMarketingCampaign error in Waitlist Signup Form',
          {
            data,
          },
        )
        props.setFormStatus(FormStatus.FAILURE)
      }
    },
    onError: (err) => {
      logger.debug(err.message, {
        reqId: 'Waitlist Signup Form',
      })

      props.setFormStatus(FormStatus.FAILURE)
    },
  })

  const handleAddProductToWaitlist = (
    values: SubmitValues,
    captchaResponse?: string,
  ) => {
    const { emailAddress: email, name } = values

    signUpForEmailMarketingCampaign({
      variables: {
        input: {
          email,
          name: showFullNameFieldOnWaitlist ? name : '',
          campaignId: props.campaignCode,
          marketingOptIn:
            props.enableSignUp === EnableSignUp.OPTIONAL
              ? values.marketingConsentCheckbox
              : props.enableSignUp === EnableSignUp.AUTO,
          auditData: {
            messageShown: props.gdprDisclaimerText || '',
            formIdentifier: 'MARKETING_CAMPAIGN',
            formLocation:
              typeof window !== 'undefined' ? window.location.href : '',
          },
        },
      },
      context: {
        headers: {
          'X-Captcha-Type': captcha.type,
          'X-Captcha-Response': captchaResponse,
        },
      },
    })
  }

  const onSubmitHandler = (fields: SubmitValues) => {
    captcha.submit((response) => handleAddProductToWaitlist(fields, response))
  }

  return (
    <React.Fragment>
      {props.title?.text && (
        <Title
          data-testid="form-title"
          horizontalAlignment={props.title.horizontalAlignment}
        >
          {props.title.text}
        </Title>
      )}
      {props.subtitle?.text && (
        <Subtitle
          extraPadding
          horizontalAlignment={props.subtitle.horizontalAlignment}
        >
          {props.subtitle.text}
        </Subtitle>
      )}
      <div ref={captchaContainerRef}>
        <DynamicForm
          csrf={csrf}
          onSubmit={onSubmitHandler}
          fields={[
            {
              fieldName: 'name',
              renderField: (
                ref: React.RefObject<HTMLInputElement>,
                key: string,
              ) =>
                showFullNameFieldOnWaitlist ? (
                  <StyledNameFormItemWrapper>
                    <FormItem bindref={ref} disableSpacing key={key}>
                      <Input
                        label={
                          props.fullNamePlaceholderText ||
                          i18nText.fullNameFieldName
                        }
                        required
                        type="text"
                        helperText={i18nText.requiredFieldText}
                        i18nText={{
                          requiredError: i18n(
                            'general.form.input.validation.required',
                          ),
                        }}
                      />
                    </FormItem>
                  </StyledNameFormItemWrapper>
                ) : null,
            },
            {
              fieldName: 'emailAddress',
              renderField: (
                ref: React.RefObject<HTMLInputElement>,
                key: string,
              ) => (
                <FormItem bindref={ref} disableSpacing key={key}>
                  <Input
                    label={
                      props.emailPlaceholderText || i18nText.emailFieldName
                    }
                    required
                    type="email"
                    helperText={i18nText.requiredFieldText}
                    i18nText={{
                      emailValidationError: i18n(
                        'general.form.input.validation.email',
                      ),
                      requiredError: i18n(
                        'general.form.input.validation.required',
                      ),
                    }}
                  />
                </FormItem>
              ),
            },
            {
              fieldName: 'marketingConsentCheckbox',
              renderField: (
                ref: React.RefObject<HTMLInputElement>,
                key: string,
              ) =>
                props.signUpCheckboxLabel &&
                props.enableSignUp === EnableSignUp.OPTIONAL ? (
                  <FormItem bindref={ref} disableSpacing key={key}>
                    <Checkbox
                      checked={false}
                      label={props.signUpCheckboxLabel}
                      required={true}
                    />
                  </FormItem>
                ) : null,
            },
          ]}
        >
          {captcha.captcha}
          <SubmitButton
            type="submit"
            emphasis="high"
            data-testid="button-submit-waitlist-signup"
          >
            {props.buttonText || i18nText.submitButtonText}
          </SubmitButton>
        </DynamicForm>
      </div>
      {props.gdprDisclaimerText && (
        <DisclaimerText>{props.gdprDisclaimerText}</DisclaimerText>
      )}
    </React.Fragment>
  )
}
